import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpParameterCodec, HttpRequest } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { OUTSIDE_APP_HEADER, SessionService, Tokens } from '../services/session.service';
import { catchError, map, takeUntil, switchMap, filter } from 'rxjs/operators';
import { HttpCancelService } from '../services/http-cancel.service';
import { AuthService } from '../services/auth.service';
import { SnackBarService } from '../services/snack-bar.service';

export const NOT_CANCELLED_HEADER: string = 'X-Request-Not-Cancellable';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

	constructor(
		private sessionService: SessionService,
		private httpCancelService: HttpCancelService,
		private authService: AuthService,
		private snackBarService: SnackBarService
	) {}

	intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
        let headers: HttpHeaders = req.headers ?? new HttpHeaders();

        let isOutsideApp: boolean = false;
        if (headers.has(OUTSIDE_APP_HEADER)) {
            isOutsideApp = true;
            headers = headers.delete(OUTSIDE_APP_HEADER);
        }
		
		if (!isOutsideApp && !req.url.includes('/locales/')) {
			headers = headers
				.set('Authorization', `Bearer ${this.sessionService.token}`)
				.set('xsrfHeaderName', 'X-CSRFToken')
				.set('xsrfCookieName', 'csrftoken')
                .set('Session-Id', this.sessionService.sessionId)
				.set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

			if (this.sessionService.clientId) {
				headers = headers.set('Client-Id', `${this.sessionService.clientId}`);
			}
		}

		let authReq = req.clone({
			headers
		});

		return next.handle(authReq)
			.pipe(
				takeUntil(this.httpCancelService.onCancelPendingRequests().pipe(
					filter(() => !authReq.headers.has(NOT_CANCELLED_HEADER))
				)),
				catchError((error: HttpErrorResponse) => {
					if (error.status === 401) {
						return from(this.authService.refreshToken())
							.pipe(
								switchMap((response: Tokens) => {
									this.sessionService.setTokens(response);
									headers = headers.set('Authorization', `Bearer ${this.sessionService.token}`);
									authReq = req.clone({
										headers
									});
									return next.handle(authReq);
								})
							);
					}

					if (error.status === 403) {
						this.snackBarService.setForbiddenErrorNotification();
					}

					throw error;
				}),
				map((response: HttpResponse<any>) => response)
			);
	}
}

export class HttpUrlEncodingCodec implements HttpParameterCodec {

	encodeKey(k: string): string {
		return standardEncoding(k);
	}

	encodeValue(v: string): string {
		return standardEncoding(v);
	}

	decodeKey(k: string): string {
		return decodeURIComponent(k);
	}

	decodeValue(v: string): string {
		return decodeURIComponent(v);
	}
}

const standardEncoding = (v: string): string => encodeURIComponent(v);
